import { ColorRamp } from "./types";

type Rgb = { r: number; g: number; b: number };

/** Parse "#rgb", "#rrggbb", "rgb(...)" or "rgba(...)" (theme palette formats). */
function parseColor(color: string): Rgb {
  const hex = color.trim();
  if (hex.startsWith("#")) {
    const body =
      hex.length === 4
        ? hex
            .slice(1)
            .split("")
            .map((c) => c + c)
            .join("")
        : hex.slice(1, 7);
    return {
      r: parseInt(body.slice(0, 2), 16),
      g: parseInt(body.slice(2, 4), 16),
      b: parseInt(body.slice(4, 6), 16),
    };
  }
  const match = hex.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (match && match[1] && match[2] && match[3]) {
    return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
  }
  return { r: 128, g: 128, b: 128 };
}

/** Linear RGB blend between two CSS colors (t in [0, 1]). Endpoints
 *  return the original strings untouched. */
export function interpolateColor(from: string, to: string, t: number): string {
  if (t <= 0) return from;
  if (t >= 1) return to;
  return mix(parseColor(from), parseColor(to), t);
}

function mix(from: Rgb, to: Rgb, t: number): string {
  const channel = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `rgb(${channel(from.r, to.r)}, ${channel(from.g, to.g)}, ${channel(
    from.b,
    to.b
  )})`;
}

/**
 * Ramp anchored on zero (neutral), with independently scaled arms:
 * `negative` color saturates at -negativeExtent, `positive` at
 * +positiveExtent. Asymmetric on purpose — a symmetric ±maxAbs domain
 * washes out the smaller side when the data is lopsided (e.g. evolutions
 * from -3% to +30%: every negative would render near-neutral).
 */
export function divergingRamp(
  negativeExtent: number,
  positiveExtent: number,
  colors: { negative: string; neutral: string; positive: string }
): ColorRamp {
  // A zero extent would create coincident stops, which the interpolation
  // expression rejects — pad with an epsilon.
  const negative = negativeExtent > 0 ? negativeExtent : 1;
  const positive = positiveExtent > 0 ? positiveExtent : 1;
  return {
    stops: [
      [-negative, colors.negative],
      [0, colors.neutral],
      [positive, colors.positive],
    ],
  };
}

/**
 * Ramp from `low` color at min to `high` color at max. For level metrics.
 *
 * `mid` inserts a waypoint color halfway: linear RGB between complementary
 * hues (red → green) passes through muddy brown, so callers route such
 * ramps through amber/yellow.
 */
export function sequentialRamp(
  min: number,
  max: number,
  colors: { low: string; mid?: string; high: string }
): ColorRamp {
  const paddedMax = max > min ? max : min + 1;
  if (colors.mid === undefined) {
    return {
      stops: [
        [min, colors.low],
        [paddedMax, colors.high],
      ],
    };
  }
  return {
    stops: [
      [min, colors.low],
      [(min + paddedMax) / 2, colors.mid],
      [paddedMax, colors.high],
    ],
  };
}

/** Evaluate a ramp at a value (clamped to the ramp's domain). */
export function colorForValue(ramp: ColorRamp, value: number): string {
  const stops = ramp.stops;
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (!first || !last) return "rgb(128, 128, 128)";
  if (value <= first[0]) return first[1];
  if (value >= last[0]) return last[1];
  for (let i = 1; i < stops.length; i++) {
    const prev = stops[i - 1];
    const next = stops[i];
    if (!prev || !next) continue;
    if (value <= next[0]) {
      const span = next[0] - prev[0];
      const t = span > 0 ? (value - prev[0]) / span : 0;
      return mix(parseColor(prev[1]), parseColor(next[1]), t);
    }
  }
  return last[1];
}
